import React, { useState, useEffect } from 'react';
import axios from './lib/axiosClient';

function NotificationLog() {
  const [notifications, setNotifications] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ channel: '', status: '', search: '' });
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [expanded, setExpanded] = useState(null);
  const limit = 25;

  useEffect(() => { fetchStats(); }, []);
  useEffect(() => { fetchNotifications(); }, [filter, page]);

  const fetchStats = async () => {
    try {
      const res = await axios.get('/api/notifications/stats');
      setStats(res.data.stats);
    } catch (err) { console.error(err); }
  };

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const params = { limit, offset: page * limit };
      if (filter.channel) params.channel = filter.channel;
      if (filter.status) params.status = filter.status;
      if (filter.search) params.search = filter.search;
      const res = await axios.get('/api/notifications', { params });
      setNotifications(res.data.notifications || []);
      setTotal(res.data.total || 0);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const statusBadge = (status) => {
    const colors = {
      sent: { bg: '#10b98120', color: '#10b981', label: '✓ Sent' },
      failed: { bg: '#ef444420', color: '#ef4444', label: '✕ Failed' },
      logged: { bg: '#f59e0b20', color: '#f59e0b', label: '⏳ Logged (no API key)' },
      queued: { bg: '#3b82f620', color: '#3b82f6', label: '◉ Queued' },
    };
    const c = colors[status] || colors.logged;
    return (
      <span style={{ background: c.bg, color: c.color, padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600 }}>
        {c.label}
      </span>
    );
  };

  const channelIcon = (ch) => ch === 'sms' ? '💬' : '📧';

  const triggerLabel = (t) => {
    const map = {
      receipt: 'Receipt', reminder: 'Reminder', review_request: 'Review Request',
      waitlist: 'Waitlist', work_order_ready: 'Bike Ready', booking_complete: 'Booking Complete',
      manual: 'Manual',
    };
    return map[t] || t || '—';
  };

  const formatDate = (d) => {
    if (!d) return '';
    const dt = new Date(d);
    return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ' ' +
           dt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  const formatBody = (body) => {
    if (!body) return '';
    // Strip HTML tags for display
    const text = body.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
    return text.length > 120 ? text.slice(0, 120) + '…' : text;
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 800, margin: 0 }}>📨 Notification Log</h1>
          <p style={{ color: '#94a3b8', margin: '4px 0 0', fontSize: 14 }}>Track all emails and texts sent to your customers</p>
        </div>
        <button onClick={() => { fetchNotifications(); fetchStats(); }}
          style={{ padding: '8px 16px', background: '#1e293b', border: '1px solid #334155', borderRadius: 8, color: '#e2e8f0', cursor: 'pointer', fontSize: 13 }}>
          ↻ Refresh
        </button>
      </div>

      {/* Stats */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 24 }}>
          {[
            { label: 'Total', value: stats.total, icon: '📊', color: '#e2e8f0' },
            { label: 'SMS', value: stats.sms, icon: '💬', color: '#3b82f6' },
            { label: 'Email', value: stats.email, icon: '📧', color: '#8b5cf6' },
            { label: 'Delivered', value: stats.sent, icon: '✓', color: '#10b981' },
            { label: 'Failed', value: stats.failed, icon: '✕', color: '#ef4444' },
          ].map((s, i) => (
            <div key={i} style={{ background: '#0f172a', borderRadius: 12, padding: 16, border: '1px solid #1e293b' }}>
              <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>{s.icon} {s.label}</div>
              <div style={{ fontSize: 28, fontWeight: 800, color: s.color }}>{s.value}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <select value={filter.channel} onChange={e => { setFilter(f => ({ ...f, channel: e.target.value })); setPage(0); }}
          style={{ padding: '8px 12px', background: '#0f172a', border: '1px solid #1e293b', borderRadius: 8, color: '#e2e8f0', fontSize: 13 }}>
          <option value="">All Channels</option>
          <option value="sms">💬 SMS</option>
          <option value="email">📧 Email</option>
        </select>
        <select value={filter.status} onChange={e => { setFilter(f => ({ ...f, status: e.target.value })); setPage(0); }}
          style={{ padding: '8px 12px', background: '#0f172a', border: '1px solid #1e293b', borderRadius: 8, color: '#e2e8f0', fontSize: 13 }}>
          <option value="">All Statuses</option>
          <option value="sent">✓ Sent</option>
          <option value="failed">✕ Failed</option>
          <option value="logged">⏳ Logged</option>
        </select>
        <input placeholder="Search recipient, subject, body..."
          value={filter.search}
          onChange={e => { setFilter(f => ({ ...f, search: e.target.value })); setPage(0); }}
          style={{ padding: '8px 12px', background: '#0f172a', border: '1px solid #1e293b', borderRadius: 8, color: '#e2e8f0', fontSize: 13, flex: 1, minWidth: 200 }}
        />
      </div>

      {/* Table */}
      <div style={{ background: '#0f172a', borderRadius: 12, border: '1px solid #1e293b', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #1e293b' }}>
              {['', 'Recipient', 'Type', 'Preview', 'Status', 'Time'].map(h => (
                <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 1 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Loading...</td></tr>
            ) : notifications.length === 0 ? (
              <tr><td colSpan={6} style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>
                No notifications yet. Notifications will appear here after bookings, checkouts, and reminders are sent.
              </td></tr>
            ) : notifications.map(n => (
              <React.Fragment key={n.id}>
                <tr onClick={() => setExpanded(expanded === n.id ? null : n.id)}
                  style={{ borderBottom: '1px solid #1e293b', cursor: 'pointer', transition: 'background 0.15s' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#1e293b40'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <td style={{ padding: '12px 16px', fontSize: 18 }}>{channelIcon(n.channel)}</td>
                  <td style={{ padding: '12px 16px', fontSize: 14, color: '#e2e8f0', fontWeight: 600 }}>
                    {n.recipient}
                    {n.subject && <div style={{ fontSize: 12, color: '#64748b', fontWeight: 400, marginTop: 2 }}>{n.subject}</div>}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <span style={{ background: '#1e293b', padding: '3px 10px', borderRadius: 6, fontSize: 12, color: '#94a3b8' }}>
                      {triggerLabel(n.trigger_type)}
                    </span>
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: 13, color: '#94a3b8', maxWidth: 300 }}>{formatBody(n.body)}</td>
                  <td style={{ padding: '12px 16px' }}>{statusBadge(n.status)}</td>
                  <td style={{ padding: '12px 16px', fontSize: 13, color: '#64748b', whiteSpace: 'nowrap' }}>{formatDate(n.created_at)}</td>
                </tr>
                {expanded === n.id && (
                  <tr>
                    <td colSpan={6} style={{ padding: '0 16px 16px', background: '#0c1222' }}>
                      <div style={{ padding: 16, borderRadius: 8, background: '#1e293b', marginTop: 4 }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                          <div><span style={{ color: '#64748b', fontSize: 11, textTransform: 'uppercase' }}>Channel</span><div style={{ color: '#e2e8f0', fontSize: 14, marginTop: 2 }}>{n.channel === 'sms' ? 'SMS Text' : 'Email'}</div></div>
                          <div><span style={{ color: '#64748b', fontSize: 11, textTransform: 'uppercase' }}>Status</span><div style={{ marginTop: 2 }}>{statusBadge(n.status)}</div></div>
                          <div><span style={{ color: '#64748b', fontSize: 11, textTransform: 'uppercase' }}>Trigger</span><div style={{ color: '#e2e8f0', fontSize: 14, marginTop: 2 }}>{triggerLabel(n.trigger_type)}</div></div>
                          <div><span style={{ color: '#64748b', fontSize: 11, textTransform: 'uppercase' }}>Sent At</span><div style={{ color: '#e2e8f0', fontSize: 14, marginTop: 2 }}>{formatDate(n.created_at)}</div></div>
                        </div>
                        {n.subject && (
                          <div style={{ marginBottom: 12 }}>
                            <span style={{ color: '#64748b', fontSize: 11, textTransform: 'uppercase' }}>Subject</span>
                            <div style={{ color: '#e2e8f0', fontSize: 14, marginTop: 2 }}>{n.subject}</div>
                          </div>
                        )}
                        <div>
                          <span style={{ color: '#64748b', fontSize: 11, textTransform: 'uppercase' }}>Full Message</span>
                          {n.channel === 'email' && n.body && n.body.includes('<') ? (
                            <div style={{ background: '#fff', borderRadius: 8, padding: 16, marginTop: 6, maxHeight: 300, overflow: 'auto' }}
                              dangerouslySetInnerHTML={{ __html: n.body }} />
                          ) : (
                            <div style={{ color: '#e2e8f0', fontSize: 14, marginTop: 4, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                              {n.body?.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim() || '(no content)'}
                            </div>
                          )}
                        </div>
                        {n.error_message && (
                          <div style={{ marginTop: 12, background: '#ef444420', borderRadius: 8, padding: 12 }}>
                            <span style={{ color: '#ef4444', fontSize: 11, textTransform: 'uppercase', fontWeight: 700 }}>Error</span>
                            <div style={{ color: '#fca5a5', fontSize: 13, marginTop: 4 }}>{n.error_message}</div>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16 }}>
          <span style={{ color: '#64748b', fontSize: 13 }}>Showing {page * limit + 1}–{Math.min((page + 1) * limit, total)} of {total}</span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button disabled={page === 0} onClick={() => setPage(p => p - 1)}
              style={{ padding: '6px 14px', background: '#1e293b', border: '1px solid #334155', borderRadius: 6, color: '#e2e8f0', cursor: page === 0 ? 'not-allowed' : 'pointer', opacity: page === 0 ? 0.4 : 1 }}>← Prev</button>
            <button disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}
              style={{ padding: '6px 14px', background: '#1e293b', border: '1px solid #334155', borderRadius: 6, color: '#e2e8f0', cursor: page >= totalPages - 1 ? 'not-allowed' : 'pointer', opacity: page >= totalPages - 1 ? 0.4 : 1 }}>Next →</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default NotificationLog;
